import React, { useState } from 'react';
import { toast } from 'sonner';
import { Clock, Languages, Video, UserCheck, ShieldCheck } from 'lucide-react';
import { aiCandidateAPI, errMsg } from '../../../lib/aiInterviewApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';

const SCOPE_TITLES = {
  recording: 'Audio & video recording',
  ai_evaluation: 'AI-conducted interview with AI-assisted, advisory scoring',
  proctoring: 'Integrity checks during the interview',
  biometric: 'ID check and face match (biometric data)',
};
export const LANG_NAMES = { en: 'English', hi: 'Hindi' };

const Facts = ({ ctx }) => (
  <div className="iv-facts" data-testid="candidate-facts">
    <div className="iv-fact"><Clock size={16} color="#0076A3" /><b>{`${Math.round(ctx.duration_min + (ctx.extra_minutes || 0))} min`}</b><span>Duration</span></div>
    <div className="iv-fact"><Languages size={16} color="#0076A3" /><b>{LANG_NAMES[ctx.language] || ctx.language}</b><span>Language</span></div>
    <div className="iv-fact"><Video size={16} color="#0076A3" /><b>Voice + video</b><span>Format</span></div>
    <div className="iv-fact"><UserCheck size={16} color="#0076A3" /><b>People decide</b><span>AI only advises</span></div>
  </div>
);

const HOW = [
  'Review and agree to how your interview is recorded and evaluated.',
  'Optional: ask for extra time, captions or a human interview instead.',
  'Quick camera, microphone and connection check.',
  'A short ID and face check that runs in your browser (if required).',
  'A conversation with the AI interviewer, one question at a time. Answer out loud, or type if you prefer.',
];

const DeclineDialog = ({ open, onOpenChange, token, onDeclined }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await aiCandidateAPI.decline(token, { reason, request_human: true });
      onDeclined();
    } catch (e) {
      toast.error(errMsg(e, 'Could not record your decision'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="iv" data-testid="candidate-decline-dialog">
        <DialogHeader>
          <DialogTitle>Decline the AI interview?</DialogTitle>
          <DialogDescription>We'll tell the hiring team you'd like an interview with a person instead. Declining does not count against you.</DialogDescription>
        </DialogHeader>
        <textarea className="iv-textarea" rows={3} placeholder="Optional: anything you'd like the team to know" value={reason}
          onChange={(e) => setReason(e.target.value)} data-testid="candidate-decline-reason-input" />
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => onOpenChange(false)} data-testid="candidate-decline-cancel-btn">Go back</button>
          <button type="button" className="iv-btn iv-btn-primary" disabled={busy} onClick={submit} data-testid="candidate-decline-confirm-btn">
            {busy ? 'Sending…' : 'Decline & request a human interview'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function ConsentStep({ ctx, token, onDone, onDeclined }) {
  const scopes = ctx.consent.scopes.filter((s) => s !== 'biometric' || ctx.identity_required);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const all = scopes.every((s) => checked[s]);

  const agree = async () => {
    setBusy(true);
    try {
      await aiCandidateAPI.consent(token, checked);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save your consent'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="iv-card" data-testid="candidate-welcome-card">
        <div className="iv-eyebrow">AI interview{ctx.company_name ? ` · ${ctx.company_name}` : ''}</div>
        <h1 className="iv-h1" data-testid="candidate-welcome-title">
          {ctx.first_name ? `Hi ${ctx.first_name}, welcome` : 'Welcome'} to your interview for {ctx.job_title}
        </h1>
        <p className="iv-sub">This is a structured, job-related conversation built from the role and your résumé. There are no trick questions, and you can ask the interviewer to repeat anything.</p>
        <Facts ctx={ctx} />
      </section>

      <section className="iv-card">
        <h2 className="iv-h2">How it works</h2>
        <ol className="iv-sub" style={{ paddingLeft: '1.1rem', display: 'grid', gap: '0.35rem' }}>
          {HOW.map((h) => <li key={h}>{h}</li>)}
        </ol>
      </section>

      <section className="iv-card" data-testid="candidate-consent-card">
        <div className="iv-row" style={{ marginBottom: '0.5rem' }}>
          <ShieldCheck size={18} color="#0076A3" />
          <h2 className="iv-h2" style={{ margin: 0 }}>Your consent</h2>
        </div>
        <p className="iv-small" style={{ marginBottom: '0.5rem' }}>Please read each item and tick to agree. Version {ctx.consent.version}.</p>
        {scopes.map((s) => (
          <label key={s} className="iv-check" data-testid={`consent-scope-${s}`}>
            <input type="checkbox" checked={!!checked[s]} onChange={(e) => setChecked((c) => ({ ...c, [s]: e.target.checked }))}
              data-testid={`consent-checkbox-${s}`} aria-label={SCOPE_TITLES[s]} />
            <span>
              <strong>{SCOPE_TITLES[s]}</strong>
              <span className="iv-small">{ctx.consent.text[s]}</span>
            </span>
          </label>
        ))}
        <div className="iv-note" style={{ marginTop: '0.75rem' }}>
          The AI's report is advisory. A person at the hiring company reviews every interview and makes every decision; integrity flags only route an interview to human review.
        </div>
        <p className="iv-small" style={{ marginTop: '0.75rem' }}>
          Read more: <a href="/legal/privacy" target="_blank" rel="noreferrer">Privacy policy</a> · <a href="/legal/biometric-policy" target="_blank" rel="noreferrer">Biometric policy</a> · <a href="/ai-transparency" target="_blank" rel="noreferrer">AI transparency</a>
        </p>
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => setDeclineOpen(true)} data-testid="candidate-decline-btn">
            Decline & request a human interview
          </button>
          <button type="button" className="iv-btn iv-btn-cta" disabled={!all || busy} onClick={agree} data-testid="candidate-consent-agree-btn">
            {busy ? 'Saving…' : 'I Agree & Continue'}
          </button>
        </div>
      </section>
      <DeclineDialog open={declineOpen} onOpenChange={setDeclineOpen} token={token} onDeclined={onDeclined} />
    </>
  );
}
